// src/pages/EmployeeExpense.js
import { useState, useEffect, useCallback, useRef } from "react";
import { auth, db, storage } from "../firebase/config";
import {
  collection,
  getDocs,
  addDoc,
  getDoc,
  doc,
  query,
  where,
  setDoc,
  serverTimestamp,
  Timestamp,
  updateDoc,
  deleteField,
  arrayUnion,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "../styles/EmployeeExpense.css";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../Context/AuthContext";

// helper: write a company-level notification card
const sendCompanyNotification = async (companyId, docId, payload) => {
  const nref = doc(db, `companies/${companyId}/notifications`, docId);
  await setDoc(
    nref,
    {
      archived: false,
      read: false,
      timestamp: serverTimestamp(),
      ...payload,
    },
    { merge: true },
  );
};

export default function EmployeeExpense() {
  const [companyId, setCompanyId] = useState(null);
  const [expenseType, setExpenseType] = useState("Travel");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(""); // YYYY-MM-DD
  const [description, setDescription] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [activeTab, setActiveTab] = useState("submit");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [merchant, setMerchant] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("reimbursable");
  const [editingId, setEditingId] = useState(null); // <- track edit mode
  // ── Expense Config (Types + Payment Methods) ────────────────────────
  const [expenseConfigLoading, setExpenseConfigLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();

  const [expenseConfig, setExpenseConfig] = useState({
    expenseTypes: [
      { id: "travel", label: "Travel", active: true },
      { id: "meals", label: "Meals", active: true },
      { id: "office_supplies", label: "Office Supplies", active: true },
      { id: "other", label: "Other", active: true },
    ],
    paymentMethods: [
      {
        id: "reimbursable",
        label: "Employee paid",
        active: true,
        value: "reimbursable",
      },
      {
        id: "company_card",
        label: "Company card",
        active: true,
        value: "company_card",
      },
    ],
  });

  // NEW: ref to reset file input
  const fileInputRef = useRef(null);

  // policy (defaults until we load company)
  const [policy, setPolicy] = useState({
    backdateDays: 180,
    receiptThreshold: 25, // still stored, but we now require receipts for *all* new expenses
    autoApproveUnder: 0,
    twoStepApprovalOver: 0,
  });

  // Format date in company timezone
  const fmt = (input) => {
    try {
      if (!input) return "—";
      if (typeof input === "string") {
        return DateTime.fromISO(input, { zone: companyTimeZone }).toFormat(
          "MMM dd, yyyy",
        );
      }
      if (input?.toDate) {
        return DateTime.fromJSDate(input.toDate(), {
          zone: companyTimeZone,
        }).toFormat("MMM dd, yyyy");
      }
      return DateTime.fromJSDate(new Date(input), {
        zone: companyTimeZone,
      }).toFormat("MMM dd, yyyy");
    } catch {
      return "—";
    }
  };

  const money = (n) =>
    isFinite(n)
      ? Number(n).toLocaleString(undefined, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        })
      : "$0.00";

  // Date bounds (policy-driven)
  const today = DateTime.now()
    .setZone(companyTimeZone)
    .startOf("day");
  const maxISO = today.toISODate();
  const minISO = today.minus({ days: policy.backdateDays }).toISODate();

  const activeExpenseTypes = (expenseConfig.expenseTypes || []).filter(
    (t) => t.active,
  );
  const activePaymentMethods = (expenseConfig.paymentMethods || []).filter(
    (m) => m.active,
  );

  // Find the user's company + policy + TZ
  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError("");

        if (authLoading) return;

        if (!user?.uid) {
          setCompanyId(null);
          setError("User not authenticated.");
          return;
        }

        const uid = user.uid;

        // ✅ 1) get companyId without scanning
        let cid = user.companyId || null;

        // fallback: users/{uid}
        if (!cid) {
          const userSnap = await getDoc(doc(db, "users", uid));
          cid = userSnap.exists() ? userSnap.data()?.companyId || null : null;
        }

        if (!cid) throw new Error("Company not found.");
        setCompanyId(cid);

        // ✅ 2) load company TZ + expense policy (single doc)
        const compDoc = await getDoc(doc(db, "companies", cid));
        const data = compDoc.exists() ? compDoc.data() : {};
        const tz = data?.timeZone || "UTC";
        setCompanyTimeZone(tz);

        const ep = data?.expensePolicy || {};
        setPolicy({
          backdateDays: Number(ep.backdateDays ?? 180),
          receiptThreshold: Number(ep.receiptThreshold ?? 25),
          autoApproveUnder: Number(ep.autoApproveUnder ?? 0),
          twoStepApprovalOver: Number(ep.twoStepApprovalOver ?? 0),
        });
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load company.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [user?.uid, user?.companyId, authLoading]);

  // Load this employee's expenses
  const fetchExpenses = useCallback(async () => {
    try {
      if (!companyId || !auth.currentUser) return;

      const qy = query(
        collection(db, `companies/${companyId}/expenses`),
        where("employeeId", "==", auth.currentUser.uid),
      );

      const snapshot = await getDocs(qy);
      const result = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort(
          (a, b) =>
            (b.expenseDateTs?.toMillis?.() ?? 0) -
            (a.expenseDateTs?.toMillis?.() ?? 0),
        );

      setExpenses(result);
    } catch (err) {
      console.error("Failed to fetch expenses:", err.message);
    }
  }, [companyId]);

  // ── Load Expense Config (Types + Payment Methods) ───────────────────
  useEffect(() => {
    const loadExpenseConfig = async () => {
      if (!companyId) return;

      try {
        setExpenseConfigLoading(true);

        const refDoc = doc(
          db,
          `companies/${companyId}/settings`,
          "expense_config",
        );
        const snap = await getDoc(refDoc);

        if (!snap.exists()) {
          // fallback to defaults already in state
          return;
        }

        const data = snap.data() || {};
        const types = Array.isArray(data.expenseTypes) ? data.expenseTypes : [];
        const methods = Array.isArray(data.paymentMethods)
          ? data.paymentMethods
          : [];

        // Normalize payment methods for UI: map to your existing values
        // Your EmployeeExpense expects paymentMethod to be "reimbursable" | "company_card"
        const normalizedMethods = methods.map((m) => ({
          id: String(m.id || "")
            .trim()
            .toLowerCase(),
          label: String(m.label || "").trim() || "Method",
          active: m.active !== false,
        }));

        setExpenseConfig({
          expenseTypes: types.map((t) => ({
            ...t,
            label: String(t.label || "").trim() || "Type",
            active: t.active !== false,
          })),
          paymentMethods: normalizedMethods,
        });
      } catch (err) {
        console.error("Failed to load expense config:", err);
      } finally {
        setExpenseConfigLoading(false);
      }
    };

    loadExpenseConfig();
  }, [companyId]);

  // If employer disables a type/method currently selected, auto-switch to first active option
  useEffect(() => {
    // Expense Type
    const typeOk = activeExpenseTypes.some((t) => t.label === expenseType);
    if (!typeOk && activeExpenseTypes.length > 0) {
      setExpenseType(activeExpenseTypes[0].label);
    }

    // Payment Method (uses .value)
    const methodOk = activePaymentMethods.some((m) => m.id === paymentMethod);
    if (!methodOk && activePaymentMethods.length > 0) {
      setPaymentMethod(activePaymentMethods[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseConfig]);

  useEffect(() => {
    if (companyId) fetchExpenses();
  }, [companyId, fetchExpenses]);

  // Load an item into the form
  const loadForEdit = (exp) => {
    setActiveTab("submit");
    setEditingId(exp.id);
    setExpenseType(exp.expenseType || "Travel");
    setAmount(String(exp.amount || ""));
    setMerchant(exp.merchant || "");
    setPaymentMethod(exp.paymentMethod || "reimbursable");
    setDate(exp.expenseDate || "");
    setDescription(exp.description || "");

    // no prefill for receipt (we keep existing receiptUrl in Firestore)
    setReceipt(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // If old values are now disabled, auto-switch to first active option
    setTimeout(() => {
      if (activeExpenseTypes.length > 0) {
        const ok = activeExpenseTypes.some(
          (t) => t.label === (exp.expenseType || "Travel"),
        );
        if (!ok) setExpenseType(activeExpenseTypes[0].label);
      }

      if (activePaymentMethods.length > 0) {
        const ok = activePaymentMethods.some(
          (m) => m.value === (exp.paymentMethod || "reimbursable"),
        );
        if (!ok) setPaymentMethod(activePaymentMethods[0].value);
      }
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      setError("");

      const user = auth.currentUser;
      if (!companyId || !user) throw new Error("Missing company or user.");

      if (!amount || Number(amount) <= 0) {
        throw new Error("Enter a valid amount greater than 0.");
      }

      const chosen = DateTime.fromISO(date, { zone: companyTimeZone }).startOf(
        "day",
      );
      if (!chosen.isValid) throw new Error("Pick a valid date.");
      if (chosen > today)
        throw new Error("Expense date cannot be in the future.");
      if (chosen < today.minus({ days: policy.backdateDays })) {
        throw new Error(
          `Expense date must be within the last ${policy.backdateDays} days.`,
        );
      }

      // Validate expenseType against active config (prevents tampering / stale UI)
      if (activeExpenseTypes.length > 0) {
        const allowedType = activeExpenseTypes.some(
          (t) => t.label === expenseType,
        );
        if (!allowedType) {
          throw new Error(
            "This expense type is not allowed. Please choose another.",
          );
        }
      }

      // Validate payment method against active config
      if (activePaymentMethods.length > 0) {
        const allowedMethod = activePaymentMethods.some(
          (m) => m.id === paymentMethod,
        );

        if (!allowedMethod) {
          throw new Error(
            "This payment method is not allowed. Please choose another.",
          );
        }
      }

      // NEW: For NEW submissions, a receipt is always required
      if (!editingId && !receipt) {
        throw new Error("A receipt is required for all expenses.");
      }

      // employee name
      const employeeRef = doc(db, `companies/${companyId}/employees`, user.uid);
      const employeeSnap = await getDoc(employeeRef);
      const employeeData = employeeSnap.exists() ? employeeSnap.data() : null;
      const employeeName =
        employeeData?.preferredFirstName ||
        employeeData?.name ||
        `${employeeData?.firstName || ""} ${employeeData?.lastName ||
          ""}`.trim() ||
        "Unknown";

      // optional receipt upload (required for new, optional for resubmit if old receipt exists)
      let receiptUrl = "";
      if (receipt) {
        const fileRef = ref(
          storage,
          `receipts/${companyId}/${user.uid}_${Date.now()}_${receipt.name}`,
        );
        await uploadBytes(fileRef, receipt);
        receiptUrl = await getDownloadURL(fileRef);
      }

      if (editingId) {
        // UPDATE existing expense (resubmission)
        const expRef = doc(db, `companies/${companyId}/expenses`, editingId);
        await updateDoc(expRef, {
          expenseType,
          amount: Number(amount),
          merchant: merchant.trim(),
          paymentMethod,
          expenseDate: chosen.toISODate(),
          expenseDateTs: Timestamp.fromDate(chosen.toJSDate()),
          description,

          ...(receiptUrl ? { receiptUrl } : {}), // keep old receipt if none uploaded
          status: "Pending", // back to pending
          openChangeRequest: deleteField(), // close the request
          reviewerNote: deleteField(),
          updatedAt: serverTimestamp(),
          revisions: arrayUnion({
            by: auth.currentUser.uid,
            type: "resubmitted",
            at: Timestamp.now(),
          }),
        });

        // Clear edit mode
        setEditingId(null);
      } else {
        // NEW submit
        const expRef = await addDoc(
          collection(db, `companies/${companyId}/expenses`),
          {
            employeeId: user.uid,
            employeeName,
            expenseType,
            amount: Number(amount),
            merchant: merchant.trim(),
            paymentMethod, // 'reimbursable' | 'company_card'
            expenseDate: chosen.toISODate(),
            expenseDateTs: Timestamp.fromDate(chosen.toJSDate()),
            description,
            receiptUrl,

            status: "Pending",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
        );
        toast.success("✅ Expense submitted!");
        // ✅ OPTIONAL: take them to the history tab immediately
        setActiveTab("history");
        // notify company reviewers
        await sendCompanyNotification(
          companyId,
          `expenseSubmitted_${expRef.id}`,
          {
            type: "expenseSubmitted",
            employeeId: user.uid,
            employeeName,
            expenseId: expRef.id,
            amount: Number(amount),
            expenseType,
            expenseDate: chosen.toISODate(),
            status: "Pending",
            message: `${employeeName} submitted an expense of ${Number(
              amount,
            ).toLocaleString(undefined, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 2,
            })} for ${expenseType}.`,
          },
        );
      }

      await fetchExpenses();

      // reset form
      setExpenseType("Travel");
      setAmount("");
      setMerchant("");
      setPaymentMethod("reimbursable");
      setDate("");
      setDescription("");
      setReceipt(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Error submitting expense:", err);
      setError(err.message || "Failed to submit expense.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="employeeexpense-container">
      <ToastContainer position="top-right" autoClose={2500} />
      <nav className="employeeexpense-nav">
        <p className="employeeexpense-breadcrumb">
          <Link to="/employee-dashboard">Dashboard</Link> /{" "}
          <span>Expenses</span>
        </p>
      </nav>

      <header className="ex-header">
        <div>
          <h1 className="ex-title">Expenses</h1>
          <div className="ex-sub">
            Submit a new expense or review your history.
          </div>
        </div>
      </header>

      <div className="employeeexpense-tabs">
        <button
          className={`employeeexpense-tab ${
            activeTab === "submit" ? "active" : ""
          }`}
          onClick={() => setActiveTab("submit")}
        >
          Submit Expense
        </button>
        <button
          className={`employeeexpense-tab ${
            activeTab === "history" ? "active" : ""
          }`}
          onClick={() => setActiveTab("history")}
        >
          Expense History
        </button>
      </div>

      {activeTab === "submit" && (
        <section className="ex-card">
          {loading ? (
            <div className="ex-hint">Loading…</div>
          ) : error ? (
            <div className="ex-hint" style={{ color: "#b91c1c" }}>
              {error}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="ex-row">
            {/* Expense type */}
            <div>
              <label className="ex-label">Expense Type</label>
              <select
                className="ex-select"
                value={expenseType}
                onChange={(e) => setExpenseType(e.target.value)}
                disabled={expenseConfigLoading}
              >
                {activeExpenseTypes.length === 0 ? (
                  <option value="Other">Other</option>
                ) : (
                  activeExpenseTypes.map((t) => (
                    <option key={t.id || t.label} value={t.label}>
                      {t.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Amount */}
            <div>
              <label className="ex-label">Amount (USD)</label>
              <input
                className="ex-input"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="0.01"
                inputMode="decimal"
                required
              />
            </div>

            {/* Merchant */}
            <div>
              <label className="ex-label">Merchant</label>
              <input
                className="ex-input"
                type="text"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                placeholder="e.g., Uber, Delta, Staples"
                required
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="ex-label">Payment Method</label>
              <select
                className="ex-select"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                disabled={expenseConfigLoading}
              >
                {activePaymentMethods.length === 0 ? (
                  <>
                    <option value="reimbursable">Employee paid</option>
                    <option value="company_card">Company card</option>
                  </>
                ) : (
                  activePaymentMethods.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Date */}
            <div>
              <label className="ex-label">Expense Date</label>
              <input
                className="ex-input"
                type="date"
                min={minISO}
                max={maxISO}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>

            {/* Description */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="ex-label">Description</label>
              <textarea
                className="ex-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                minLength={5}
                maxLength={300}
                required
              />
              <div className="ex-hint">{description.length}/300 characters</div>
            </div>

            {/* Receipt */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="ex-label">Receipt (required)</label>
              <input
                className="ex-file"
                type="file"
                accept="image/*,application/pdf"
                ref={fileInputRef}
                onChange={(e) => setReceipt(e.target.files?.[0] || null)}
              />
              {receipt && (
                <div className="ex-hint">Selected: {receipt.name}</div>
              )}
              <div className="ex-hint">
                Upload a receipt (image or PDF). Required for all expenses.
              </div>
            </div>

            {/* Submit */}
            <div style={{ gridColumn: "1 / -1" }}>
              <button
                type="submit"
                className="btn btn-block"
                disabled={!companyId || loading || submitting}
              >
                {submitting
                  ? "Submitting…"
                  : editingId
                  ? "Resubmit"
                  : "Submit Expense"}
              </button>
            </div>
          </form>
        </section>
      )}

      {activeTab === "history" && (
        <section className="ex-card">
          <h3 className="ex-title" style={{ fontSize: 18, marginBottom: 10 }}>
            My Submitted Expenses
          </h3>

          {loading ? (
            <div className="ex-hint">Loading…</div>
          ) : expenses.length === 0 ? (
            <div className="ex-empty">No expenses submitted yet.</div>
          ) : (
            <div className="ex-list">
              {expenses.map((exp) => {
                const statusClass =
                  exp.status === "Approved"
                    ? "status-appr"
                    : exp.status === "Rejected"
                    ? "status-rej"
                    : "status-pend";

                return (
                  <div key={exp.id} className="ex-item">
                    <div
                      className="ex-inline"
                      style={{ justifyContent: "space-between" }}
                    >
                      <h4>
                        {exp.expenseType}{" "}
                        <span className="ex-meta">• {money(exp.amount)}</span>
                      </h4>
                      <span className={`pill ${statusClass}`}>
                        {exp.status}
                      </span>
                    </div>

                    <div className="ex-meta">
                      {fmt(exp.expenseDate || exp.expenseDateTs)} • Submitted{" "}
                      {fmt(exp.createdAt)}
                    </div>

                    {exp.description && (
                      <div className="ex-hint">{exp.description}</div>
                    )}

                    {exp.status === "NeedsChanges" &&
                      (exp.openChangeRequest?.note || exp.reviewerNote) && (
                        <div className="ex-hint">
                          <strong>Reviewer note:</strong>{" "}
                          {exp.openChangeRequest?.note || exp.reviewerNote}
                        </div>
                      )}

                    {exp.status === "NeedsChanges" && (
                      <button
                        className="doc-link"
                        onClick={() => loadForEdit(exp)}
                        style={{ marginTop: 6 }}
                      >
                        Edit & Resubmit
                      </button>
                    )}

                    {exp.receiptUrl && (
                      <a
                        className="ex-hint"
                        href={exp.receiptUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View receipt ↗
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {error && activeTab === "submit" && (
        <p className="employeeexpense-error" style={{ marginTop: 12 }}>
          {error}
        </p>
      )}
    </div>
  );
}
